import { useCallback, useEffect, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import type { ApiResponseBackupSummary } from '../types/api/ApiResponseBackup';
import type { BackupInventorySummary } from '../types/models/Backup';

/**
 * `GET /datacenters/backups/summary` — totals and freshness for the page's card row.
 *
 * Two contract details that shape this hook:
 *
 * - The payload sits **flat under `data`** (no wrapping key), unlike every other backup endpoint.
 * - It is the only place `stale_after_hours` is published, and the backend echoes it explicitly
 *   *so the frontend never hardcodes the threshold*. Everything that renders a "Stale" verdict
 *   takes the value from here.
 *
 * All-zero counts with `null` datetimes and `targets: []` is a **success** — "no backups" is a
 * legitimate state, not an error.
 */
export function useBackupSummary() {
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const [summary, setSummary] = useState<BackupInventorySummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);

  const fetchSummary = useCallback(async () => {
    if (!auth.user?.access_token) return;

    setLoading(true);
    setError(null);

    try {
      const response = await apiFetch('/datacenters/backups/summary');
      const json = (await response.json().catch(() => null)) as ApiResponseBackupSummary | null;

      if (json === null) {
        throw new Error(`Unexpected response from server (HTTP ${response.status})`);
      }
      if (json.status !== 'success' || !json.data) {
        throw new Error(json.message ?? 'Unexpected response from server');
      }

      setSummary(json.data);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('An unexpected error occurred'));
    } finally {
      setLoading(false);
    }
  }, [auth.user?.access_token, apiFetch]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  return { summary, loading, error, refetch: fetchSummary };
}
